'use client';

import { useState } from 'react';
import Image from 'next/image';
import { ArrowRightIcon } from '@heroicons/react/24/outline';
import { SpeakerModal } from './SpeakerModal';
import { type Speaker } from '../../events';
import placeholder from './placeholder-headshot.png';

export function SpeakerCard({ speaker }: { speaker: Speaker }) {
  const [isSpeakerModalOpen, setIsSpeakerModalOpen] = useState(false);
  const openSpeakerModal = () => setIsSpeakerModalOpen(true);

  return (
    <>
      <SpeakerModal speaker={speaker} isOpen={isSpeakerModalOpen} onClose={() => setIsSpeakerModalOpen(false)} />
      <button
        onClick={openSpeakerModal}
        className="group relative flex h-44 w-full max-w-lg cursor-pointer flex-col justify-center gap-x-2 rounded-2xl border border-[#2E3135] bg-[#202328] p-6 md:h-48"
      >
        <div className="col-span-2 flex items-center gap-4">
          <div className="relative flex items-center justify-center">
            <Image
              src={speaker.headshotLink ? `/assets/images/events/event-speakers/${speaker.headshotLink}` : placeholder}
              alt={speaker.name}
              className="size-20 md:size-[120px]"
              width={80}
              height={80}
            />
            {speaker.company === 'Gradle' && <GradlePillLogo />}
          </div>

          <div className="mt-1 flex flex-col gap-1 self-start text-left md:mt-3">
            <h6 className="text-h6 sm:underline-text-gradient md:group-hover:underline-text-gradient w-fit">
              {speaker.name}
            </h6>
            <p className="text-body w-fit text-wrap text-gray-4 lg:whitespace-nowrap">{speaker.title}</p>
          </div>
        </div>
        <div className="absolute bottom-6 right-6 col-start-3 row-start-3 w-fit rounded-full bg-gray-3 p-[0.625rem] transition-colors group-hover:bg-white sm:bg-white">
          <ArrowRightIcon className="size-6 text-[#0F172A]" />
        </div>
      </button>
    </>
  );
}

function GradlePillLogo() {
  return (
    <div className="group absolute -bottom-4 flex h-8 w-12 items-center justify-start overflow-hidden rounded-2xl border-2 border-white bg-gradient-to-br from-gradle-blue to-gradle-green transition-all duration-300 group-hover:w-20">
      <div className="flex w-32 items-center justify-between gap-6 px-2.5 transition-transform duration-300 group-hover:-translate-x-[2.9rem]">
        <svg viewBox="0 0 43 35" fill="none" className="h-4 w-6 min-w-6" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M42.49 2.47475C39.7752 -0.242242 35.3945 -0.315712 32.59 2.30873C32.455 2.43296 32.3779 2.60782 32.3773 2.79126C32.3735 2.97552 32.4446 3.1534 32.5745 3.2842L33.4721 4.18184C33.7113 4.42007 34.0892 4.4466 34.3594 4.2441C35.0632 3.71295 35.9215 3.42684 36.8032 3.42948C39.0585 3.42944 40.8868 5.25765 40.8868 7.51292C40.8868 8.59469 40.4576 9.63227 39.6933 10.3979C34.0532 16.0691 26.4829 0.222872 9.35508 8.3743C8.19076 8.91513 7.68531 10.2974 8.22614 11.4618C8.25403 11.5218 8.28444 11.5806 8.31734 11.638L11.2541 16.7177C11.8918 17.8174 13.2932 18.203 14.4037 17.5842L14.4763 17.5427L14.4192 17.5842L15.7216 16.8526C17.1744 15.9529 18.5475 14.9304 19.8259 13.7965C20.0856 13.5542 20.4844 13.5409 20.7598 13.7654C21.0464 13.9989 21.0894 14.4205 20.8559 14.7071C20.8272 14.7422 20.795 14.7744 20.7598 14.8031C19.4258 16.0045 17.9901 17.0878 16.4688 18.0408L16.4221 18.0668L15.1197 18.7932C14.5707 19.1043 13.9502 19.267 13.3192 19.2654C11.9885 19.2687 10.7567 18.5628 10.0867 17.413L7.30555 12.6186C1.97158 16.3856 -1.26618 23.6446 0.472039 32.8286C0.534275 33.1488 0.815197 33.3796 1.14138 33.3786H4.30128C4.65154 33.392 4.95369 33.1349 4.99658 32.7871C5.32771 30.2467 7.65556 28.4558 10.1959 28.7869C12.2822 29.0589 13.9242 30.7008 14.1961 32.7871C14.2375 33.121 14.5186 33.3734 14.8551 33.3786H17.9372C18.2787 33.3789 18.5674 33.1257 18.6117 32.7871C18.9315 30.2481 21.2489 28.4491 23.7879 28.7689C25.8878 29.0334 27.5416 30.6872 27.8061 32.7871C27.848 33.1249 28.135 33.3786 28.4754 33.3786H31.5212C31.8897 33.3786 32.19 33.0829 32.1957 32.7144C32.2683 28.4182 33.4254 23.4838 36.7306 21.0139C48.1405 12.463 45.1363 5.13137 42.49 2.47475ZM30.831 15.4102V15.405L28.6518 14.3102C28.649 13.5536 29.26 12.9381 30.0165 12.9353C30.7731 12.9325 31.3886 13.5435 31.3914 14.3C31.393 14.7384 31.1847 15.1511 30.831 15.4102Z"
            fill="white"
          ></path>
        </svg>
        <p className="text-[18px] font-bold">Gradle</p>
      </div>
    </div>
  );
}
